'use client';

import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import type { LucideIcon } from 'lucide-react';
import {
  BellIcon,
  CheckCheckIcon,
  ClockIcon,
  CloudCheckIcon,
  InfoIcon,
  Loader2Icon,
  RocketIcon,
  Settings2Icon,
  ShieldAlertIcon,
  ShoppingBagIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useNotifications } from '@/features/notifications/presentation/providers/notifications-provider';
import { cn } from '@/shared/utils/utils';

function notificationIcon(type: string): { icon: LucideIcon; color: string } {
  const key = type.toLowerCase();
  const map: Record<string, { icon: LucideIcon; color: string }> = {
    order: {
      icon: ShoppingBagIcon,
      color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-500',
    },
    security: {
      icon: ShieldAlertIcon,
      color: 'bg-red-500/10 text-red-600 dark:text-red-500',
    },
    system: {
      icon: RocketIcon,
      color: 'bg-violet-500/10 text-violet-600 dark:text-violet-500',
    },
    backup: {
      icon: CloudCheckIcon,
      color: 'bg-blue-500/10 text-blue-600 dark:text-blue-500',
    },
    warning: {
      icon: TriangleAlertIcon,
      color: 'bg-amber-500/10 text-amber-600 dark:text-amber-500',
    },
  };
  return (
    map[key] ?? {
      icon: InfoIcon,
      color: 'bg-neutral-500/10 text-neutral-600 dark:text-neutral-400',
    }
  );
}

export function NotificationsPopover() {
  const {
    notifications,
    unreadCount,
    isLoading,
    isError,
    browserPermission,
    markAsRead,
    markAllAsRead,
    requestBrowserNotifications,
    refetch,
  } = useNotifications();

  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="group relative rounded-xl border-neutral-200 transition-all hover:bg-neutral-50 active:scale-95 dark:border-neutral-800 dark:hover:bg-neutral-900"
          aria-label="Notificaciones"
        >
          <BellIcon className="size-4 text-neutral-500 transition-transform group-hover:scale-110" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 overflow-hidden rounded-3xl border border-neutral-100 bg-white p-0 shadow-none transition-all dark:border-neutral-800 dark:bg-neutral-950"
      >
        <div className="flex flex-col">
          <div className="flex items-center justify-between gap-4 border-b border-neutral-50 bg-neutral-50/50 px-4 py-3 dark:border-neutral-800 dark:bg-neutral-950">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                Notificaciones
              </span>
              <div className="shrink-0 rounded-full border border-orange-500/20 bg-orange-500/10 px-1.5 py-0.5 text-[9px] leading-none font-bold text-orange-600 dark:bg-orange-500/20 dark:text-orange-500">
                {unreadCount} sin leer
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                className="size-7 rounded-lg p-0 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-emerald-500 dark:hover:bg-neutral-800"
                disabled={unreadCount === 0}
                onClick={() => markAllAsRead()}
                title="Marcar todas como leídas"
              >
                <CheckCheckIcon className="size-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="size-7 rounded-lg p-0 text-neutral-400 transition-colors hover:bg-neutral-100 dark:hover:bg-neutral-800"
                title="Activar alertas del navegador"
                onClick={() => void requestBrowserNotifications()}
              >
                <Settings2Icon className="size-3.5" />
              </Button>
            </div>
          </div>

          {typeof window !== 'undefined' && 'Notification' in window && browserPermission === 'denied' && (
            <p className="border-b border-amber-500/20 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-800 dark:text-amber-200">
              Las notificaciones del navegador están bloqueadas. Habilítalas en la configuración del sitio.
            </p>
          )}

          {typeof window !== 'undefined' && 'Notification' in window && browserPermission === 'default' && (
            <p className="border-b border-neutral-100 px-3 py-2 text-[11px] text-neutral-600 dark:border-neutral-800 dark:text-neutral-400">
              Pulsa el icono de ajustes para permitir alertas del navegador cuando lleguen avisos nuevos.
            </p>
          )}

          <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto p-2">
            {isLoading && (
              <li className="flex items-center justify-center gap-2 py-8 text-sm text-neutral-500">
                <Loader2Icon className="size-5 animate-spin" />
                Cargando…
              </li>
            )}
            {isError && (
              <li className="rounded-2xl border border-red-500/20 bg-red-500/5 px-3 py-3 text-[12px] text-red-700 dark:text-red-400">
                No se pudieron cargar las notificaciones.
                <Button type="button" variant="link" className="h-auto p-0 pl-1 text-[12px]" onClick={() => refetch()}>
                  Reintentar
                </Button>
              </li>
            )}
            {!isLoading && !isError && notifications.length === 0 && (
              <li className="py-8 text-center text-[12px] text-neutral-500">No hay notificaciones recientes.</li>
            )}
            {!isLoading &&
              !isError &&
              notifications.map(item => {
                const { icon: Icon, color } = notificationIcon(item.type);
                const unread = item.read_at === null;
                const relative = formatDistanceToNow(new Date(item.created_at), {
                  addSuffix: true,
                  locale: es,
                });

                return (
                  <li key={item.id} className="list-none">
                    <button
                      type="button"
                      className={cn(
                        'group relative flex w-full cursor-pointer items-start gap-3 rounded-2xl border px-3 py-2.5 text-left transition-all',
                        unread
                          ? 'border-neutral-100 bg-neutral-50/50 dark:border-neutral-800 dark:bg-white/5'
                          : 'border-transparent bg-transparent hover:border-neutral-100 hover:bg-neutral-50 dark:hover:border-neutral-800 dark:hover:bg-white/5'
                      )}
                      onClick={() => {
                        if (unread) markAsRead(item.id);
                      }}
                    >
                      <div
                        className={cn(
                          'flex size-8 shrink-0 items-center justify-center rounded-xl border border-transparent transition-all group-hover:scale-105',
                          color
                        )}
                      >
                        <Icon className="size-4" />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0">
                        <div className="line-clamp-2 text-[12px] leading-tight font-semibold tracking-tight text-neutral-900 dark:text-neutral-100">
                          {item.title}
                        </div>
                        <div className="line-clamp-2 text-[11px] font-normal text-neutral-600 dark:text-neutral-400">
                          {item.body}
                        </div>
                        <div className="mt-0.5 flex items-center justify-between gap-2">
                          <p className="truncate text-[9px] font-medium text-neutral-500">{item.type}</p>
                          <div className="flex shrink-0 items-center gap-1.5 opacity-60">
                            <ClockIcon className="size-2.5 text-neutral-400" />
                            <p className="text-[9px] font-normal tracking-tight whitespace-nowrap text-neutral-500">
                              {relative}
                            </p>
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
          </ul>

          <div className="border-t border-neutral-100 p-3 dark:border-neutral-800">
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl border-neutral-200 bg-white text-[11px] font-semibold text-neutral-500 shadow-none transition-all hover:text-orange-500 active:scale-95 dark:border-neutral-800 dark:bg-neutral-950"
              onClick={() => refetch()}
            >
              Actualizar lista
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
